// src/components/navbar/Navbar.jsx
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { IconButton } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HandshakeIcon from '@mui/icons-material/Handshake';
import EventIcon from '@mui/icons-material/Event';
import PeopleIcon from '@mui/icons-material/People';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ArticleIcon from '@mui/icons-material/Article';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SettingsIcon from '@mui/icons-material/Settings';
import SmartphoneIcon from '@mui/icons-material/Smartphone';

import { useAuth } from '../../context/authContext';
import DesktopMenu from './DesktopMenu';
import MobileDrawer from './MobileDrawer';
import EconomicInfo from './EconomicInfo';
import LogoCarousel from './LogoCarousel';
import { getMyCompany } from '../../services/companiesLookupService';
import { getAppMovilConfig } from '../../services/appMovilConfigService';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [company, setCompany] = useState(null);
  const [appMovilEnabled, setAppMovilEnabled] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (!user) return setCompany(null);
        const data = await getMyCompany();
        if (alive) setCompany(data || null);
      } catch {
        if (alive) setCompany(null);
      }
    })();
    return () => { alive = false; };
  }, [user]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const cfg = await getAppMovilConfig();
        if (alive) setAppMovilEnabled(cfg.enabled);
      } catch {
        if (alive) setAppMovilEnabled(false);
      }
    };
    load();
    const onUpdated = () => load();
    window.addEventListener('app-movil-config-updated', onUpdated);
    return () => {
      alive = false;
      window.removeEventListener('app-movil-config-updated', onUpdated);
    };
  }, []);

  const toggleDrawer = useCallback((open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) return;
    setDrawerOpen(open);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      setIsLoggingOut(true);
      await logout();
      navigate('/', { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  }, [logout, navigate]);

  const menuItems = useMemo(() => {
    const actualidadGroup = {
      text: 'NOTICIAS Y EVENTOS',
      icon: <ArticleIcon />,
      children: [
        { text: 'Noticias', path: '/news', icon: <ArticleIcon /> },
        { text: 'Calendario de Eventos', path: '/events', icon: <EventIcon /> },
      ],
    };
    const finanzasGroup = {
      text: 'INFORMACIÓN FINANCIERA',
      icon: <ArticleIcon />,
      children: [
        { text: 'Reportes anuales', path: '/annual-reports', icon: <ArticleIcon /> },
      ],
    };

    if (!user) {
      return [
        actualidadGroup,
        { text: 'CONVENIOS', path: '/agreements', icon: <HandshakeIcon /> },
        { text: 'SOCIOS', path: '/companies', icon: <PeopleIcon /> },
        { text: 'VINCULACIÓN COMERCIAL', path: '/services', icon: <PersonAddIcon /> },
        finanzasGroup,
        { text: 'INICIAR SESIÓN', path: '/', icon: <AccountCircleIcon /> },
      ];
    }

    const items = [
      actualidadGroup,
      { text: 'CONVENIOS', path: '/agreements', icon: <HandshakeIcon /> },
      { text: 'SOCIOS', path: '/companies', icon: <PeopleIcon /> },
      { text: 'VINCULACIÓN COMERCIAL', path: '/services', icon: <PersonAddIcon /> },
      finanzasGroup,
    ];
    if (user?.role === 'admin' || appMovilEnabled) {
      items.push({ text: 'APP MÓVIL', path: '/download-app', icon: <SmartphoneIcon /> });
    }
    if (user?.role === 'admin') {
      items.push({ text: 'ÁREA DE TRABAJO', path: '/panel', icon: <SettingsIcon /> });
    }
    return items;
  }, [user, appMovilEnabled]);

  const userDisplayName =
    company?.trade_name ||
    company?.business_name ||
    user?.business_name ||
    user?.name ||
    'Usuario';
  const userAvatarUrl = company?.logo || '';

  return (
    <>
      {/* Header sticky — sin offsets extras */}
      <div className="sticky top-0 z-50 w-full">
        {/* Barra principal */}
        <div className="w-full h-32 bg-gradient-to-r from-[#09274A] to-[#0488AF] flex items-center justify-between px-4 md:px-8 shadow-md overflow-visible relative z-30">
          {/* Logo */}
          <div
            className="w-32 h-32 md:w-32 md:h-32 flex justify-center items-center cursor-pointer pr-2 md:pr-4"
            onClick={() => navigate('/home')}
          >
            <img
              src="/RDS - LOGO VERTICAL-06.png"
              alt="Logo Red de Socios CIDERE"
              className="h-full w-auto object-contain"
            />
          </div>

          {/* Hamburguesa móvil */}
          <div className="md:hidden">
            <IconButton
              edge="start"
              color="inherit"
              aria-label="Abrir menú"
              aria-expanded={drawerOpen}
              aria-controls="mobile-menu"
              onClick={toggleDrawer(true)}
              sx={{ color: '#fff' }}
            >
              <MenuIcon />
            </IconButton>
          </div>

          {/* Menú desktop */}
          <DesktopMenu
            menuItems={menuItems}
            user={user}
            location={location}
            navigate={navigate}
            onLogout={handleLogout}
            userDisplayName={userDisplayName}
            userAvatarUrl={userAvatarUrl}
          />
        </div>

        {/* Drawer móvil */}
        <MobileDrawer
          drawerOpen={drawerOpen}
          toggleDrawer={toggleDrawer}
          menuItems={menuItems}
          user={user}
          navigate={navigate}
          onLogout={handleLogout}
          location={location}
          userDisplayName={userDisplayName}
          userAvatarUrl={userAvatarUrl}
        />

        {/* Franja económica (sin márgenes) */}
        <div className="m-0 p-0">
          <EconomicInfo />
        </div>
      </div>

      {/* Carrusel inmediatamente debajo — SIN margin-top */}
      <LogoCarousel />
    </>
  );
};

export default React.memo(Navbar);
